import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, InputLabel, LinearProgress,
  MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableContainer,
  TableHead, TextField, Typography
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import CreditScoreIcon from '@mui/icons-material/CreditScore';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.common.black, color: theme.palette.common.white },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));
const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dateFR = (value) => value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—';
const dh = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Dhs`;

export default function Manques() {
  const userData = useSelector((state) => state.userData);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const id = userData.id;
  const apitoken = userData.apitoken;
  const [vendeurs, setVendeurs] = useState([]);
  const [vendeur, setVendeur] = useState('Tout');
  const [debut, setDebut] = useState(() => localDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [fin, setFin] = useState(() => localDate(new Date()));
  const [manques, setManques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    let active = true;
    axios.post(`${apiUrl}vendeurs/${id}/${apitoken}`, {}, { timeout: 20000 })
      .then(({ data }) => { if (active) setVendeurs(data); })
      .catch((error) => { if (error.response?.status === 401) navigate('/'); else setNotification({ severity: 'error', message: 'Impossible de charger les vendeurs' }); });
    return () => { active = false; };
  }, [apiUrl, id, apitoken, navigate]);

  const loadManques = useCallback(async (signal) => {
    if (!debut || !fin || debut > fin) { setManques([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${apiUrl}manques/versements/${id}/${apitoken}/${debut}/${fin}`, {}, { signal, timeout: 20000 });
      if (!signal?.aborted) setManques(data);
    } catch (error) {
      if (signal?.aborted || error.code === 'ERR_CANCELED') return;
      if (error.response?.status === 401) navigate('/');
      else setNotification({ severity: 'error', message: 'Impossible de charger les manques' });
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [apiUrl, id, apitoken, debut, fin, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    loadManques(controller.signal);
    return () => controller.abort();
  }, [loadManques]);

  const filtered = useMemo(() => manques.filter((m) => vendeur === 'Tout' || m.vendeur?.id === vendeur), [manques, vendeur]);
  const totals = useMemo(() => filtered.reduce((acc, m) => {
    const montant = Number(m.montant || 0);
    if (m.statut === 'solde') acc.solde += montant;
    else if (m.statut === 'ouvert') acc.ouvert += montant;
    return acc;
  }, { ouvert: 0, solde: 0 }), [filtered]);

  const regler = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${apiUrl}regler/manque/versement/${id}/${apitoken}/${selected._id}`, {}, { timeout: 20000 });
      if (data.status === 'ok') {
        setSelected(null);
        setNotification({ severity: 'success', message: 'Manque réglé avec succès' });
        await loadManques();
      }
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
      else setNotification({ severity: 'error', message: error.response?.data?.detail || 'Impossible de régler ce manque' });
    } finally { setSaving(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: '40px', mb: 5 }}>
      <Typography variant="h4" align="center" color="primary">Manques des vendeurs</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 5, mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="manques-vendeur-label">Vendeur</InputLabel>
          <Select labelId="manques-vendeur-label" value={vendeur} label="Vendeur" onChange={(e) => setVendeur(e.target.value)}>
            <MenuItem value="Tout">Tous les vendeurs</MenuItem>
            {vendeurs.map((v) => <MenuItem key={v._id} value={v._id}>{v.nom}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField type="date" label="Date début" value={debut} onChange={(e) => setDebut(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="Date fin" value={fin} onChange={(e) => setFin(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
      {debut > fin && <Alert severity="warning" sx={{ mb: 2 }}>La date de début doit précéder la date de fin.</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}><Typography color="text.secondary">Total des manques ouverts</Typography><Typography variant="h6" color="error">{dh(totals.ouvert)}</Typography></Paper>
        <Paper sx={{ p: 2, flex: 1 }}><Typography color="text.secondary">Total récupéré</Typography><Typography variant="h6" color="success.main">{dh(totals.solde)}</Typography></Paper>
      </Stack>
      <TableContainer component={Paper}>
        {(loading || saving) && <LinearProgress color="success" />}
        <Table sx={{ minWidth: 800 }}>
          <TableHead><TableRow>
            <StyledTableCell>Date</StyledTableCell><StyledTableCell>Vendeur</StyledTableCell>
            <StyledTableCell>Montant</StyledTableCell><StyledTableCell>Raison</StyledTableCell>
            <StyledTableCell>Statut</StyledTableCell><StyledTableCell>Date récupération</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </TableRow></TableHead>
          <TableBody>
            {!loading && filtered.length === 0 && <StyledTableRow><StyledTableCell colSpan={7} align="center">Pas de données</StyledTableCell></StyledTableRow>}
            {filtered.map((m) => <StyledTableRow key={m._id}>
              <StyledTableCell>{dateFR(m.date)}</StyledTableCell>
              <StyledTableCell>{m.vendeur?.nom || '—'}</StyledTableCell>
              <StyledTableCell>{dh(m.montant)}</StyledTableCell>
              <StyledTableCell>{m.raison || '—'}</StyledTableCell>
              <StyledTableCell><Chip size="small" color={m.statut === 'solde' ? 'success' : 'warning'} label={m.statut === 'solde' ? 'Soldé' : 'Ouvert'} /></StyledTableCell>
              <StyledTableCell>{dateFR(m.date_recuperation)}</StyledTableCell>
              <StyledTableCell align="right">{m.statut === 'ouvert' && <Button variant="outlined" color="warning" startIcon={<CreditScoreIcon />} disabled={saving} onClick={() => setSelected(m)}>Régler</Button>}</StyledTableCell>
            </StyledTableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={Boolean(selected)} onClose={() => !saving && setSelected(null)}>
        <DialogTitle>Confirmer le règlement</DialogTitle>
        <DialogContent><DialogContentText>Confirmer la récupération intégrale de <strong>{dh(selected?.montant)}</strong> pour <strong>{selected?.vendeur?.nom}</strong> ?</DialogContentText></DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setSelected(null)}>Annuler</Button><Button variant="contained" disabled={saving} onClick={regler}>Confirmer</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notification)} autoHideDuration={5000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={notification?.severity || 'info'} variant="filled" onClose={() => setNotification(null)}>{notification?.message}</Alert>
      </Snackbar>
    </Container>
  );
}
